<template>
  <div class="form-field">
    <div class="form-control" :class="[`direction-${props.direction}`, `label-position-${props.labelPosition}`]">
      <Label v-if="props.label" :for="props.id">{{ props.label }}</Label>
      <slot></slot>
    </div>

    <Text variant="body-small" color="secondary" v-if="props.hint && !hasError">
      {{ props.hint }}
    </Text>

    <FormFieldErrors :error="props.error" />
  </div>
</template>

<script setup lang="ts">
import { v4 as uuid } from 'uuid';
import { computed, provide } from 'vue';

import Label from '../label.vue';
import Text from '../text.vue';
import FormFieldErrors from './form-field-errors.vue';
import { FORM_FIELD_TOKEN } from './form-field.token';

interface FormFieldProps {
  id?: string;
  direction?: 'row' | 'column';
  labelPosition?: 'before' | 'after';
  label?: string;
  hint?: string;
  error?: string | string[];
}

const props = withDefaults(defineProps<FormFieldProps>(), {
  id: () => `form-field-${uuid()}`,
  direction: 'column',
  labelPosition: 'before',
});

const errors = computed<string[]>(() => {
  if (!props.error) {
    return [];
  }

  return Array.isArray(props.error) ? props.error : [props.error];
});

const hasError = computed(() => Boolean(errors.value.length));

provide(FORM_FIELD_TOKEN, {
  id: props.id,
  error: props.error,
  hasError,
});
</script>

<style scoped lang="scss">
.form-field {
  display: inline-flex;
  flex-direction: column;
  gap: var(--t-spacing-sm);
}

.form-control {
  display: inline-flex;
  gap: var(--t-spacing-sm);
  width: 100%;

  &.direction-column {
    flex-direction: column;

    &.label-position-after {
      flex-direction: column-reverse;
    }
  }

  &.direction-row {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    &.label-position-after {
      flex-direction: row-reverse;
    }
  }
}
</style>
